// ─────────────────────────────────────────────────────────────────────────────
// Service screen helpers (Phase 7.3).
//
// Pure functions only — easy to test, no React, no DOM.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute the live waiting seconds for an order, given:
 *  - the server's idea of "now" at last poll (response.serverTime)
 *  - the server's computed waitingSeconds at that instant
 *  - the client's wall clock right now
 *
 * Why not just use waitingSeconds directly:
 *   It only updates on poll. Between polls (up to 5s) the displayed timer
 *   would freeze. By layering local elapsed time on top of the server's
 *   anchor, the timer ticks every second without extra requests.
 *
 * Why not use only client time:
 *   The client's clock may differ from the server's by minutes (no NTP on
 *   tablets is common). Anchoring to the server's reading at poll time
 *   keeps the displayed value honest no matter how skewed the client is.
 */
export function liveWaitingSeconds(
  serverTime: string | null,
  serverWaitingSeconds: number,
  clientNow: Date = new Date(),
): number {
  if (!serverTime) return serverWaitingSeconds;
  const serverDate = new Date(serverTime);
  // If serverTime is unparseable (defensive), fall back to the server value.
  if (Number.isNaN(serverDate.getTime())) return serverWaitingSeconds;
  const driftMs = clientNow.getTime() - serverDate.getTime();
  // Negative drift (client clock behind server) gets clamped — never go
  // below the server's stated waiting time, since we know the order has
  // been waiting at least that long.
  if (driftMs < 0) return serverWaitingSeconds;
  return serverWaitingSeconds + Math.floor(driftMs / 1000);
}

/**
 * Format a duration in seconds as a compact string.
 *
 *   45        → "45s"
 *   90        → "1:30"
 *   3661      → "1h 1m"
 *   90061     → "1d 1h"
 *
 * Targets quick-glance kitchen UX: minimal characters, no leading zeros,
 * humans should parse it in 200 ms.
 */
export function formatWaitingTime(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) {
    const remSec = s % 60;
    return `${minutes}:${String(remSec).padStart(2, '0')}`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remMin = minutes % 60;
    return `${hours}h ${remMin}m`;
  }
  const days = Math.floor(hours / 24);
  const remHr = hours % 24;
  return `${days}d ${remHr}h`;
}

/**
 * The same duration split into the four boxes the legacy card shows:
 * Day(s) : Hour(s) : Min : Sec.
 *
 * `formatWaitingTime` above is the compact form for a glance; this is the one
 * the legacy screen prints, and people who have used that screen for years read
 * the four boxes faster than they read "1h 13m". Both are on the card.
 */
export interface WaitingParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function waitingParts(totalSeconds: number): WaitingParts {
  const s = Math.max(0, Math.floor(totalSeconds));
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor(s / 3600) % 24,
    minutes: Math.floor(s / 60) % 60,
    seconds: s % 60,
  };
}

/**
 * Pick a color band by elapsed time:
 *   - green  : < 5 minutes
 *   - amber  : 5–10 minutes
 *   - red    : > 10 minutes
 *
 * Thresholds match the prototype that Mohamed shared. Phase 7.4 will
 * surface these as design tokens; for the skeleton we return the band
 * name and let CSS map it.
 */
export type WaitingBand = 'fresh' | 'aging' | 'late';

export function waitingBand(seconds: number): WaitingBand {
  if (seconds < 5 * 60) return 'fresh';
  if (seconds < 10 * 60) return 'aging';
  return 'late';
}
